import type { JSX } from "react";
import Button from "../../atoms/button";
import Icon from "../../atoms/icon";
import Text from "../../atoms/text";
import type { ButtonIconProps, ButtonsProps } from "@/components/types";
import { faIconSpinner } from "./types";

const IconBtn = ({ loading, name, ...props }: ButtonsProps) => (
  <Icon name={loading ? faIconSpinner : name} {...props} />
);

/**
 * ButtonIcon component - A button with optional leading and trailing icons.
 *
 * @description This component extends the base `Button` to include support for icons
 * on the left and right of the button's content. It also features a loading state
 * that replaces the icons with a spinner.
 *
 * @param {ButtonIconProps} props - The props for the ButtonIcon component.
 * @param {React.ReactNode | string} [props.children] - The content of the button. Can be a string or any React node.
 * @param {IconProps} [props.iconLeft] - The icon to display on the left side of the button content.
 * @param {IconProps} [props.iconRight] - The icon to display on the right side of the button content.
 * @param {boolean} [props.loading=false] - If true, displays a spinner icon in place of the regular icons.
 * @param {ClassName} [props.className] - Custom styles for the button container.
 * @param {string} [props.styleText] - Custom styles for the text when `children` is a string.
 * @param {ButtonProps} props.props - Additional props inherited from the base `Button` component.
 *
 * @returns {JSX.Element} The rendered button with icons.
 *
 * @example
 * // Button with a left icon
 * <ButtonIcon iconLeft={{ name: 'heart' }}>
 *   Like
 * </ButtonIcon>
 *
 * @example
 * // Button with a right icon and loading state
 * <ButtonIcon iconRight={{ name: 'arrow-forward' }} loading={true}>
 *   Submitting
 * </ButtonIcon>
 *
 * @example
 * // Button with both icons and custom styles
 * <ButtonIcon
 *   iconLeft={{ name: 'person', className: { color: 'white' } }}
 *   iconRight={{ name: 'chevron-down', className: { color: 'white' } }}
 *   className={{ backgroundColor: 'blue', padding: 15 }}
 *   styleText="text-white"
 * >
 *   Profile
 * </ButtonIcon>
 *
 * @since 1.0.0
 * @author Facundo Cachan
 */
const ButtonIcon = ({
  children,
  iconLeft,
  iconRight,
  styleText,
  loading = false,
  ...props
}: ButtonIconProps): JSX.Element => (
  <Button
    {...props}
    name={props.name || iconLeft?.name || iconRight?.name}
    action={props.action}
  >
    {iconLeft && <IconBtn {...iconLeft} loading={loading} />}
    {typeof children === "string" ? (
      <Text className={styleText}>{children}</Text>
    ) : (
      children
    )}
    {iconRight && <IconBtn {...iconRight} loading={loading} />}
  </Button>
);

export default ButtonIcon;
